import React from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'antd';
import Library from '~components/property-gallery/library';
import { DragLayer } from '~components/sortable-gallery';
import update from 'immutability-helper';
import axios from 'axios';
import { pmsEndpoints as endpoints } from '~settings/endpoints';
import { fireCustomEvent } from '~helpers/custom-events';
import {
  formatBytes,
  getFileInfo,
  getFileType,
  formatLibraries,
  librariesAsArr,
  formatList,
} from '~helpers/gallery';
import { getHeader, cloneObject } from '~helpers';
import gallery, { uploadStatus } from '~constants/gallery';

export default class SpecificFloorPlanPhotoCard extends React.Component {
  static LIBRARY_MEDIA_DEFAULT = {
    minSize: null,
    types: ['image'],
    multiple: true,
    required: false,
  };

  constructor(props) {
    super(props);
    this.state = {};
    this.state.libraries = this.formatLibraries();
    this.state.list = this.formatList();

    this.dragLayer = new DragLayer();
    this.libraryElements = {};
  }

  componentDidMount() {
    this.props.onRef(this);
  }

  componentWillUnmount() {
    this.props.onRef(undefined);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.floor !== this.props.floor) {
      this.setState({
        list: this.formatList(),
        libraries: this.formatLibraries(),
      });
    }
  }

  isPropertyLoaded = () => !!this.props.property.id;

  formatLibraries = () => {
    const { property } = this.props;
    return formatLibraries(this.isPropertyLoaded() ? property.unitTypes.edges : [], property.links);
  };

  formatList = () => {
    const modifyProperty = {
      ...this.props.property,
      allImages: {
        edges: [
          ...this.props.property.allImages.edges,
          ...this.props.floor.floorPlanImages.map(i => ({ node: i })),
        ],
      },
    };
    if (this.isPropertyLoaded()) {
      const list = formatList(this.state.libraries, modifyProperty);
      return list;
    } else {
      return {};
    }
  };

  onSortEnd = ({ from, fromIndexes, to, toIndex }) => {
    let hasImage = false;
    const { list } = this.state;
    const fromList = list[from].slice(0);
    const toList = from === to ? fromList : list[to].slice(0);
    const selectedIndexes = [];
    const movingList = this.spliceItems(fromList, fromIndexes);
    // add moving list to the target list
    movingList.forEach((item, index) => {
      if (getFileType(item.contentType) === 'image') {
        hasImage = true;
      }
      toList.splice(toIndex + index, 0, item);
      selectedIndexes.push(toIndex + index);
    });

    const sort = () => {
      const error = this.verifyList(to, toList);
      if (error) {
        this.showError(error, () => {
          this.libraryElements[from].addSelectedIndexes(fromIndexes);
        });
      } else if (from === to) {
        this.setState(
          update(this.state, {
            list: {
              [to]: { $set: toList },
            },
          }),
        );
      } else {
        this.setState(
          update(this.state, {
            list: {
              [from]: { $set: fromList },
              [to]: { $set: toList },
            },
          }),
        );
      }
    };

    if (from !== to && ['property:general', 'property:room'].includes(to) && hasImage) {
      const { t } = this.props;
      Modal.confirm({
        content: t('cms:cms.properties.edit.gallery.library.drap_to_other_sections', {
          types: 'images',
        }),
        okText: t('cms:cms.listing.modal.option.yes'),
        cancelText: t('cms:cms.listing.modal.option.no'),
        onOk: sort,
        onCancel: () => {
          this.libraryElements[from].addSelectedIndexes(fromIndexes);
        },
      });
    } else {
      sort();
    }

    if (from !== to) {
      if (from === 'property:cover_photo') {
        this.deleteCoverPhoto();
      }
      if (to === 'property:cover_photo' && toList && toList[0]) {
        this.addCoverPhoto(toList[0]);
      }
    }
    this.props.onUpdateList(this.state.list, this.props.localId);
  };

  showError = (error, callback = null) => {
    Modal.error({
      content: error,
      okText: this.props.t('cms:cms.properties.edit.gallery.uploading.button.got_it'),
      onOk: callback,
    });
  };

  verifyList = (id, newList) => {
    const { t } = this.props;
    const { list } = this.state;
    const library = this.librariesAsArr().find(l => l.id === id);
    let error = null;
    if (!library.multiple && (list[id].length > 0 || newList.length > 1)) {
      error = t(library.errorKeys.multiple);
    } else {
      newList.some(item => {
        const media = gallery.media[getFileType(item.contentType)];
        if (item.size !== null && media.size < item.size) {
          error = t(media.errorKeys.size, {
            size: formatBytes(media.size),
          });
          return true;
        } else if (library.minSize) {
          const invalidSize = item.width < library.minSize[0] || item.height < library.minSize[1];
          if (invalidSize) {
            error = t(library.errorKeys.measure, {
              width: library.minSize[0],
              height: library.minSize[1],
            });
            return true;
          }
        }
        return false;
      });
    }
    return error;
  };

  librariesAsArr = () => librariesAsArr(this.state.libraries);

  onDropFiles = async (id, files) => {
    if (files.length) {
      const promiseInfo = [];
      files.forEach(file => {
        promiseInfo.push(getFileInfo(file));
      });
      const newUploadFiles = await Promise.all(promiseInfo);

      const allUploadFilesError = this.verifyList(id, newUploadFiles);
      if (allUploadFilesError) {
        this.showError(allUploadFilesError);
        return true;
      }

      newUploadFiles.map(uploadFile => {
        const formatUploadFile = {
          ...uploadFile,
          uploadState: this.getUploadStatus(uploadFile),
          progress: 0,
          cancelSource: null,
          response: null,
          fileReaded: false,
        };

        const singleFileError = this.verifyList(id, [formatUploadFile]);
        if (singleFileError) {
          this.showError(singleFileError);
          return true;
        }

        this.state.list[id].push(formatUploadFile);
        this.setState(this.state);

        if (this.getUploadStatus(uploadFile) === uploadStatus.EXCEED) {
          return true;
        }

        if (getFileType(uploadFile.contentType) === 'image') {
          this.uploadImage(formatUploadFile, id);
        }

        return true;
      });
    }

    return true;
  };

  onUpdate = (id, newList) => {
    this.setState(
      update(this.state, {
        list: {
          [id]: { $set: newList },
        },
      }),
    );
  };

  setLibrary = (id, e) => {
    this.libraryElements[id] = e;
  };

  getUploadStatus = list => {
    const media = gallery.media[getFileType(list.contentType)];

    if (list.size !== null && media.size < list.size) {
      return uploadStatus.EXCEED;
    }

    return uploadStatus.IN_PROGRESS;
  };

  isHaveCoverPhoto = () => {
    const { property } = this.props;
    return property.allImages.edges.some(image => image.node && image.node.hero);
  };

  uploadImage = (item, id) => {
    this.updateStatus(item, uploadStatus.IN_PROGRESS, 0, id);
    const data = new FormData();
    data.append('document', item.file);
    data.append('discriminator', 'FloorPlanImage');
    //Just ignore this deprecated msg,if you try Buffer will throw err.
    data.append('floor_plan_id', JSON.parse(atob(this.props.floor.id)).id);
    data.append('property_id', JSON.parse(atob(this.props.property.id)).id);

    if (id.split(':')[0] === 'property') {
      if (id.split(':')[1] === 'cover_photo') {
        data.append('category', 'general');
        if (!this.isHaveCoverPhoto()) {
          data.append('hero', true);
        }
      } else {
        data.append('category', id.split(':')[1]);
      }
    } else if (id.split(':')[0] === 'room') {
      data.append('category', 'room');
      data.append('unit_type_id', JSON.parse(atob(id.split(':')[1])).id);
    }

    // eslint-disable-next-line no-param-reassign

    const cancelSource = axios.CancelToken.source();
    const config = {
      cancelToken: cancelSource.token,
      onUploadProgress: progressEvent => {
        const progress = Math.floor((progressEvent.loaded * 100) / progressEvent.total);
        this.updateStatus(item, uploadStatus.IN_PROGRESS, progress, id);
      },
      headers: getHeader(),
      timeout: (item.type === 'video' ? 3600 : 600) * 1000,
    };
    axios
      .post(endpoints.uploadPropertyImage.url(), data, config)
      .then(response => {
        if (!response.data) {
          throw new Error('unknown error.');
        }
        const responseData = response.data;
        const itemToUpdate = Object.assign(item, {
          filename: responseData.filename,
          extension: responseData.extension,
          contentType: responseData.content_type,
          width: responseData.width,
          height: responseData.height,
          size: responseData.size,
          imageHash: responseData.image_hash,
          id: this.encodeId(responseData.id, 'Image'),
          category: responseData.category.toUpperCase(),
        });
        this.updateStatus(itemToUpdate, uploadStatus.SUCCESS, 100, id);

        if (id === 'property:cover_photo') {
          this.addCoverPhoto(item);
        }
      })
      .catch(() => {
        this.updateStatus(item, uploadStatus.FAILED, 0, id);
      });
    return cancelSource;
  };

  updateStatus = (item, uploadState, progress = 100, id) => {
    if (!this.state.list[id]) {
      return false;
    }
    this.state.list[id] = this.state.list[id].map(image => {
      if (image.uuid === item.uuid) {
        return Object.assign(image, { uploadState, progress });
      }
      return image;
    });
    this.setState(this.state);
    this.props.onUpdateList(this.state.list, this.props.localId);
    fireCustomEvent('galleryUploadProgress', this.state.list);
    return true;
  };

  onCancelFile = (item, id) => {
    const data = item;
    this.updateStatus(item, uploadStatus.FAILED, 0, id);
    if (data.cancelSource) {
      const errorMessage = 'Operation canceled by the user.';
      data.cancelSource.cancel(errorMessage);
      data.cancelSource = null;
    }
  };

  onReloadImage = (item, id) => {
    const data = item;
    data.cancelSource = this.uploadImage(item, id);
  };

  onDeleteFile = (item, id) => {
    this.state.list[id].splice(this.state.list[id].indexOf(item), 1);

    this.setState(
      update(this.state, {
        list: {
          [id]: { $set: [...this.state.list[id]] },
        },
      }),
      () => {
        fireCustomEvent('galleryUploadProgress', this.state.list);
      },
    );

    this.props.onUpdateList(this.state.list, this.props.localId);

    if (id === 'property:cover_photo') {
      this.deleteCoverPhoto();
    }
  };

  addCoverPhoto = item => {
    const { property } = this.props;
    const cloneAllImages = cloneObject(property.allImages);

    cloneAllImages.edges.push({
      node: Object.assign(item, {
        hero: true,
      }),
    });

    this.props.setProperty({
      property: Object.assign(property, {
        allImages: cloneAllImages,
      }),
    });
  };

  deleteCoverPhoto = () => {
    const { property } = this.props;
    const cloneAllImages = cloneObject(property.allImages);

    /* eslint-disable no-param-reassign */
    cloneAllImages.edges.map(image => {
      if (image.node && image.node.hero) {
        image.node.deleted = true;
      }
      return true;
    });

    this.props.setProperty({
      property: Object.assign(property, {
        allImages: cloneAllImages,
      }),
    });
  };

  spliceItems = (items, indexes) => {
    const splicedItems = [];
    const indexesSorted = indexes.sort((a, b) => a - b);
    // get the moving list
    indexesSorted.forEach(index => {
      splicedItems.push(items[index]);
    });
    // delete moving list from the source list
    indexesSorted.forEach((index, count) => {
      items.splice(index - count, 1);
    });
    return splicedItems;
  };

  encodeId = (id, type) => {
    if (typeof id === 'number') {
      return btoa(JSON.stringify({ type, id }));
    }

    return id;
  };

  isLibrariesError = () => {
    let error = false;
    Object.values(this.libraryElements).forEach(element => {
      if (element && element.validate && element.validate().validateStatus !== 'success') {
        error = true;
      }
    });
    return error;
  };

  isShouldDisabledUpload = () => {
    return this.state.list['property:specific'].length >= 5;
  };

  render() {
    const { t } = this.props;
    const { libraries, list } = this.state;
    const library = libraries.property.find(p => p.id === 'property:specific');
    return (
      <div
        className="photos-videos floor-plan-photo-library"
        ref={e => this.dragLayer.setScrollContainer(e)}
      >
        <Library
          ref={e => {
            this.setLibrary('property:specific', e);
          }}
          id={library.id}
          key={library.id}
          list={list['property:specific']}
          title={
            <div>
              <span>
                {t('cms:cms.properties.edit.gallery.library.title.specific.floor', {
                  floorIndex: this.props.floor.floorIndex,
                })}
              </span>
              <span className="floor-plan-photo-library__title__des">
                {t('cms:cms.properties.edit.gallery.library.title.specific.block', {
                  block: this.props.floor.block,
                })}
              </span>
            </div>
          }
          dropzoneDescription={t(
            'cms:cms.property.listing_management.gallery.cover_photo.dropzone_description',
          )}
          reminder={
            library.reminderTransKey &&
            t(library.reminderTransKey, {
              width: library.minSize[0],
              height: library.minSize[1],
            })
          }
          media={{
            ...SpecificFloorPlanPhotoCard.LIBRARY_MEDIA_DEFAULT,
            ...library,
          }}
          dragLayer={this.dragLayer}
          onSortEnd={this.onSortEnd}
          onDropFiles={this.onDropFiles}
          onUpdate={this.onUpdate}
          cover={library.cover}
          t={t}
          onChangeLocale={this.handleChangeLocale}
          canDrop={() => true}
          onCancel={this.onCancelFile}
          onReload={this.onReloadImage}
          onDelete={this.onDeleteFile}
          disabledUpload={this.isShouldDisabledUpload()}
          //this className not exist,just a placeholder.
          selectedClassName="disallow-select"
        />
      </div>
    );
  }
}

SpecificFloorPlanPhotoCard.propTypes = {
  t: PropTypes.func.isRequired,
  property: PropTypes.object.isRequired,
  onRef: PropTypes.func,
  floor: PropTypes.object.isRequired,
  onUpdateList: PropTypes.func,
  localId: PropTypes.string,
};

SpecificFloorPlanPhotoCard.defaultProps = {
  t: () => {},
  property: {},
  onRef: () => {},
  floor: {},
  onUpdateList: () => {},
  localId: '',
};
